<script setup lang="ts">
import { ref, computed } from 'vue'
import CompanyDetails from './registration/CompanyDetails.vue'
import UserDetails from './registration/UserDetails.vue'
import ConfirmationStep from './registration/ConfirmationStep.vue'
import FeatureList from '../home/FeatureList.vue'
import { api } from '../../api/Api'
import { tokenManager } from '../../api/utils/tokenManager'
import { getErrorMessage } from '../../utils/errorMessages'
import IconComponent from '../ui/common/IconComponent.vue'

const currentStep = ref(1)
const showCompletion = ref(false)
const formData = ref({
  company: {} as Record<string, any>,
  user: {} as Record<string, any>,
  confirmation: {} as Record<string, any>
})

const steps = [
  { 
    number: 1, 
    title: 'Company Details', 
    component: CompanyDetails,
    icon: 'Building2'
  },
  { 
    number: 2, 
    title: 'User Details', 
    component: UserDetails,
    icon: 'Users'
  },
  { 
    number: 3, 
    title: 'Confirmation', 
    component: ConfirmationStep,
    icon: 'FileCheck'
  }
]

const progress = computed(() => ((currentStep.value - 1) / (steps.length - 1)) * 100)

const handleStepComplete = (data: Record<string, any>) => {  
  switch (currentStep.value) {
    case 1:
      formData.value.company = data
      break
    case 2:
      formData.value.user = data
      break
    case 3:
      formData.value.confirmation = data
      handleSubmit()
      return
  }
  currentStep.value++
}

const error = ref('')
const isLoading = ref(false)

const handleSubmit = async () => {
  try {
    // Prepare API data
    const apiData = {
      company_name: formData.value.company.company_name,
      business_types_id: formData.value.company.business_types_id,
      countrys_id: formData.value.company.countrys_id,
      number_of_employees: formData.value.company.number_of_employees,
      primary_contact_person: formData.value.company.primary_contact_person,
      primary_email: formData.value.company.primary_email,
      primary_contact_number: formData.value.company.primary_contact_number,
      no_of_outlets: formData.value.company.no_of_outlets,
      require_accounts: formData.value.company.require_accounts,
      require_crm: formData.value.company.require_crm,
      require_hrm: formData.value.company.require_hrm,
      packages_id: formData.value.company.packages_id,
      referred_by: formData.value.company.referred_by,
      remarks: formData.value.company.remarks,
      name: formData.value.user.name,
      email: formData.value.user.email,
      password: formData.value.user.password,
      confirm_password: formData.value.user.confirm_password,
      mobile_number: formData.value.user.mobile_number,
      profile_photo: formData.value.user.profile_photo,
      cover_photo: formData.value.user.cover_photo,
      profile_text: formData.value.user.profile_text,
      paid_support: formData.value.confirmation.paidSupport,
      newsletter: formData.value.confirmation.newsletter,
      captcha: formData.value.confirmation.captchaToken
    }

    // Log the API data
    error.value = ''
    isLoading.value = true
    
    const response = await api.post('register', apiData);
    console.log(response)
    if (response.success == true) {
      // Show completion screen
      tokenManager.setMessage("new_company_registered")
      window.location.href = '/'      
    } else {
      //MessageBox.show('Unknow error occuered while trying to register')
    }

    
    //showCompletion.value = true

  } catch (err) {
    error.value = getErrorMessage(err)
    console.error('Registration error:', err)
  }
}

defineEmits<{
  (e: 'complete'): void
  (e: 'signin'): void
}>()

const getCurrentStepData = computed(() => {
  switch (currentStep.value) {
    case 1:
      return formData.value.company
    case 2:
      return formData.value.user
    case 3:
      return formData.value.confirmation
    default:
      return {}
  }
})

const handleStepClick = (stepNumber: number) => {
  // Only allow going back to previous steps
  if (stepNumber < currentStep.value) {
    currentStep.value = stepNumber
  }
}
</script>

<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
    <template v-if="!showCompletion">
      <!-- Progress bar -->
      <div class="h-2 bg-gray-200 dark:bg-gray-700">
        <div 
          class="h-full bg-blue-600 transition-all duration-300"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>

      <!-- Steps indicator -->
      <div class="px-8 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between">
          <div 
            v-for="step in steps" 
            :key="step.number"
            class="flex items-center"
          >
            <div class="flex flex-col items-center">
              <button 
                class="w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-colors mb-1"
                :class="[
                  currentStep === step.number
                    ? 'bg-blue-600 text-white'
                    : currentStep > step.number
                      ? 'bg-green-500 text-white cursor-pointer'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                ]"
                @click="handleStepClick(step.number)"
              >
                <IconComponent :name="step.icon" />
                <!-- Green Checkmark for Completed Steps -->
                <IconComponent 
                  v-if="currentStep > step.number"
                  name="CheckCircle" 
                  class="w-5 h-5 text-green-600 absolute -top-1 -right-1"
                />
                <!--<span v-else>{{ step.icon }}</span>-->
              </button>
              <span 
                class="text-sm font-medium whitespace-nowrap"
                :class="[
                  currentStep === step.number
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                ]"
              >
                {{ step.title }}
              </span>
            </div>
            
            <!-- Connector line between steps -->
            <div 
              v-if="step.number !== steps.length"
              class="flex-1 h-0.5 mx-4 mt-6"
              :class="[
                currentStep > step.number
                  ? 'bg-green-500'
                  : 'bg-gray-200 dark:bg-gray-700'
              ]"
            ></div>
          </div>
        </div>
      </div>

      <!-- Step content -->
      <div class="p-8">
        <component 
          :is="steps[currentStep - 1].component"
          :data="getCurrentStepData"
          :company="formData.company"
          :user="formData.user"
          :confirmation="formData.confirmation"
          @complete="handleStepComplete"
          @signin="$emit('signin')"
        />
      </div>
    </template>

    <!-- Completion Screen -->
    <template v-else>
      <div class="p-8 text-center">
        <div class="mb-8">
          <div class="w-20 h-20 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">
            ✓
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Registration Completed Successfully!
          </h2>
          <p class="text-gray-600 dark:text-gray-400 mb-4">
            An activation email has been sent to {{ formData.user.email }}
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Please check your email and click the activation link to complete your registration.
          </p>
        </div>

        <!-- Features List -->
        <div class="mt-12">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            While you wait, check out our features
          </h3>
          <FeatureList />
        </div>
      </div>
    </template>
  </div>
</template>